import type { FC } from 'react';
import { useMemo, useState } from 'react';
import { Button, Divider, Dropdown, Input, Popover, Skeleton, Space, Typography } from 'antd';
import type { MenuProps } from 'antd';
import { capitalCase } from 'change-case';
import { TbDeviceFloppy, TbEdit, TbLibrary, TbLock, TbSettingsCog, TbTrash, TbWorld } from 'react-icons/tb';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import DropdownButton from '@/shared/components/dropdowns/DropdownButton';
import DropdownOptionContent from '@/shared/components/dropdowns/DropdownOptionContent';
import DropdownPanel, { DropdownMenuLabel, DropdownPanelMenu } from '@/shared/components/dropdowns/DropdownPanel';
import LearnMoreFooter from '@/shared/components/layouts/footers/LearnMoreFooter';
import EmptySection from '@/shared/components/layouts/views/EmptySection';
import { SmallTooltip } from '@/shared/components/tooltips';
import type { SavedSeries } from '@/types';

import { financialStatementSectionLabel } from '../../financialSeries';
import type { FinancialSeriesSnapshot, FinancialStatementDefaults } from '../../financialSeries';
import FinancialStatementPreferencesModal from '../FinancialStatementPreferencesModal';

interface FinancialSeriesToolbarProps {
    deletingSavedSeriesId?: string;
    defaults: FinancialStatementDefaults;
    onApplySeries: (series: SavedSeries) => void;
    onCreateSeries: (name: string, snapshot: FinancialSeriesSnapshot) => Promise<unknown>;
    onDeleteSeries: (id: string) => Promise<unknown>;
    onDefaultsChange: (defaults: FinancialStatementDefaults) => void;
    onUpdateSeries: (id: string, name: string) => Promise<unknown>;
    loadingSeries?: boolean;
    savedSeries: SavedSeries[];
    savingSeries?: boolean;
    snapshot: FinancialSeriesSnapshot;
    updatingSavedSeriesId?: string;
}

const FinancialSeriesToolbar: FC<FinancialSeriesToolbarProps> = ({
    deletingSavedSeriesId,
    defaults,
    onApplySeries,
    onCreateSeries,
    onDeleteSeries,
    onDefaultsChange,
    onUpdateSeries,
    loadingSeries = false,
    savedSeries,
    savingSeries = false,
    snapshot,
    updatingSavedSeriesId,
}) => {
    const { token } = useAntDesignTheme();
    const [saveOpen, setSaveOpen] = useState(false);
    const [preferencesOpen, setPreferencesOpen] = useState(false);
    const [name, setName] = useState('');
    const [editingSeriesId, setEditingSeriesId] = useState<string>();
    const [editedSeriesName, setEditedSeriesName] = useState('');
    const compatibleSeries = useMemo(
        () => savedSeries.filter((series) => series.definition.sectionKey === snapshot.sectionKey),
        [savedSeries, snapshot.sectionKey],
    );
    const suggestedName = `${financialStatementSectionLabel(snapshot.sectionKey)} · ${snapshot.lineItems
        .map((lineItem) => capitalCase(lineItem))
        .join(', ')}`;

    const saveSeries = async () => {
        if (savingSeries) {
            return;
        }

        const resolvedName = name.trim() || suggestedName;

        try {
            await onCreateSeries(resolvedName, snapshot);
            setName('');
            setSaveOpen(false);
        } catch {
            // The shared mutation reports the error and leaves this editor open for a retry.
        }
    };

    const deleteSeries = async (id: string) => {
        if (deletingSavedSeriesId) {
            return;
        }

        try {
            await onDeleteSeries(id);
        } catch {
            // The shared mutation reports the error and keeps the saved series available for retry.
        }
    };

    const updateSeries = async (id: string) => {
        const resolvedName = editedSeriesName.trim();

        if (!resolvedName || updatingSavedSeriesId) {
            return;
        }

        try {
            await onUpdateSeries(id, resolvedName);
            setEditingSeriesId(undefined);
            setEditedSeriesName('');
        } catch {
            // The shared mutation reports the error and leaves this editor open for a retry.
        }
    };

    const items: MenuProps['items'] = compatibleSeries.map((series) => ({
        key: series.id,
        label: (
            <DropdownOptionContent
                avatar={{
                    icon: <TbLibrary size='0.875rem' />,
                    props: { shape: 'square', size: 20 },
                }}
                extra={
                    series.canEdit ? (
                        <Space.Compact size='small'>
                            <Popover
                                arrow={false}
                                content={
                                    <Space.Compact style={{ width: '18rem' }}>
                                        <Input
                                            // This click-opened naming field is the primary action in the compact editor.
                                            // eslint-disable-next-line jsx-a11y/no-autofocus
                                            autoFocus
                                            placeholder='Series Name...'
                                            size='small'
                                            value={editedSeriesName}
                                            onChange={(event) => setEditedSeriesName(event.target.value)}
                                            onPressEnter={() => void updateSeries(series.id)}
                                        />
                                        <Button
                                            disabled={!editedSeriesName.trim()}
                                            loading={updatingSavedSeriesId === series.id}
                                            size='small'
                                            type='primary'
                                            onClick={() => void updateSeries(series.id)}
                                        >
                                            Save
                                        </Button>
                                    </Space.Compact>
                                }
                                open={editingSeriesId === series.id}
                                placement='left'
                                trigger='click'
                                onOpenChange={(open) => {
                                    setEditingSeriesId(open ? series.id : undefined);
                                    setEditedSeriesName(open ? series.name : '');
                                }}
                            >
                                <SmallTooltip title='Edit Series'>
                                    <Button
                                        aria-label={`Edit saved series ${series.name}`}
                                        disabled={Boolean(deletingSavedSeriesId)}
                                        icon={<TbEdit size='0.875rem' />}
                                        size='small'
                                        type='text'
                                        onClick={(event) => {
                                            event.preventDefault();
                                            event.stopPropagation();
                                            setEditingSeriesId(series.id);
                                            setEditedSeriesName(series.name);
                                        }}
                                    />
                                </SmallTooltip>
                            </Popover>
                            <SmallTooltip title='Delete Series'>
                                <Button
                                    danger
                                    aria-label={`Delete saved series ${series.name}`}
                                    disabled={Boolean(deletingSavedSeriesId || updatingSavedSeriesId)}
                                    icon={<TbTrash size='0.875rem' />}
                                    loading={deletingSavedSeriesId === series.id}
                                    size='small'
                                    type='text'
                                    onClick={(event) => {
                                        event.preventDefault();
                                        event.stopPropagation();
                                        void deleteSeries(series.id);
                                    }}
                                />
                            </SmallTooltip>
                        </Space.Compact>
                    ) : undefined
                }
                metadata={[
                    <span key='period'>{capitalCase(series.definition.period)}</span>,
                    <span key='line-items'>
                        {series.definition.lineItems.length}{' '}
                        {series.definition.lineItems.length === 1 ? 'Line Item' : 'Line Items'}
                    </span>,
                    ...(series.definition.comparisonCompanies.length
                        ? [
                              <span key='comparisons'>
                                  {series.definition.comparisonCompanies.length}{' '}
                                  {series.definition.comparisonCompanies.length === 1 ? 'Comparison' : 'Comparisons'}
                              </span>,
                          ]
                        : []),
                    <span key='visibility' style={{ alignItems: 'center', display: 'inline-flex', gap: '0.25rem' }}>
                        {series.visibility === 'workspace' ? <TbWorld size='0.75rem' /> : <TbLock size='0.75rem' />}
                        {capitalCase(series.visibility)}
                    </span>,
                ]}
                title={
                    <Typography.Text ellipsis style={{ display: 'block', maxWidth: '14rem' }}>
                        {series.name}
                    </Typography.Text>
                }
            />
        ),
    }));

    const selectSeriesPanel = (
        <DropdownPanel footer={<LearnMoreFooter topic='saved financial series' variant='dropdown' />} width='20rem'>
            <DropdownMenuLabel>Saved Series</DropdownMenuLabel>
            {loadingSeries ? (
                <div style={{ padding: '0.75rem' }}>
                    <Skeleton active paragraph={{ rows: 3 }} title={false} />
                </div>
            ) : items.length ? (
                <DropdownPanelMenu
                    items={items}
                    onClick={({ key }) => {
                        const selected = compatibleSeries.find((series) => series.id === key);

                        if (selected) {
                            onApplySeries(selected);
                        }
                    }}
                />
            ) : (
                <EmptySection
                    description='Save the current companies, line items, date range, and period to reuse them here.'
                    height='12rem'
                    icon={<TbLibrary size='1.25rem' />}
                    label='No Saved Series'
                />
            )}
        </DropdownPanel>
    );

    return (
        <>
            <Space size={0}>
                <Space.Compact>
                    <Popover
                        arrow={false}
                        content={
                            <Space.Compact style={{ width: '22rem' }}>
                                <Input
                                    // This click-opened naming field is the primary action in the compact save popover.
                                    // eslint-disable-next-line jsx-a11y/no-autofocus
                                    autoFocus
                                    placeholder={suggestedName}
                                    size='small'
                                    value={name}
                                    onChange={(event) => setName(event.target.value)}
                                    onPressEnter={() => void saveSeries()}
                                />
                                <Button
                                    loading={savingSeries}
                                    size='small'
                                    type='primary'
                                    onClick={() => void saveSeries()}
                                >
                                    Save
                                </Button>
                            </Space.Compact>
                        }
                        open={saveOpen}
                        placement='bottomRight'
                        trigger='click'
                        onOpenChange={setSaveOpen}
                    >
                        <Button disabled={!snapshot.lineItems.length} icon={<TbDeviceFloppy />} size='small'>
                            Save Series
                        </Button>
                    </Popover>
                    <Dropdown
                        popupRender={() => selectSeriesPanel}

                        placement='bottomRight'
                        trigger={['click']}
                    >
                        <DropdownButton icon={<TbLibrary />} loading={loadingSeries} size='small'>
                            Select Series
                            {compatibleSeries.length ? (
                                <span
                                    style={{
                                        background: token.colorFillSecondary,
                                        borderRadius: token.borderRadiusSM,
                                        color: token.colorTextSecondary,
                                        fontSize: token.fontSizeSM,
                                        lineHeight: 1,
                                        padding: '0.1875rem 0.3125rem',
                                    }}
                                >
                                    {compatibleSeries.length}
                                </span>
                            ) : null}
                        </DropdownButton>
                    </Dropdown>
                </Space.Compact>
                <Divider style={{ marginInline: '0.75rem' }} type='vertical' />
                <Button
                    aria-label='Financial series preferences'
                    icon={<TbSettingsCog />}
                    size='small'
                    onClick={() => setPreferencesOpen(true)}
                />
            </Space>
            <FinancialStatementPreferencesModal
                defaults={defaults}
                open={preferencesOpen}
                onCancel={() => setPreferencesOpen(false)}
                onSave={(nextDefaults) => {
                    onDefaultsChange(nextDefaults);
                    setPreferencesOpen(false);
                }}
            />
        </>
    );
};

export default FinancialSeriesToolbar;
